import { yupResolver } from '@hookform/resolvers/yup';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState as useReactState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';

import styles from './ContactUs.module.css';
import { config } from '../../../state/config';
import { ContactDetails, Page } from '../../../state/types';
import { useState } from '../../../state/store';
import { contactDataSchema } from '../../../state/validation';
import { sendContact } from '../../../state/network';

const initialState: ContactDetails = {
  site: config.tradeInSource,
  name: '',
  email: '',
  phone: '',
  message: '',
};

interface FieldProps {
  label: string;
  error?: string;
  register: () => {
    name: string; // TODO: add better type
  };
  onBlur: () => void;
}

const Field = ({ label, register, error, onBlur }: FieldProps) => {
  const registerProps = { ...register() };
  const id = `inline-${registerProps.name}`;
  return (
    <div className="mb-6">
      <div>
        <label className="block text-gray-500 font-bold mb-1 md:mb-0 pr-4" htmlFor={id}>
          {label}:
        </label>
      </div>
      <div>
        <div>
          <input
            {...registerProps}
            onBlur={onBlur}
            className="appearance-none border-2 border-gray-200 rounded w-full py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-blue-500"
            id={id}
            type="text"
          />
        </div>
        <div className="pt-2 text-red-400">
          <div key="form-error">{error}</div>
        </div>
      </div>
    </div>
  );
};

const ContactUs: FC = () => {
  const { page, requestSent, dispatch } = useState('page', 'requestSent');

  const [contactData, setContactData] = useReactState<ContactDetails>(initialState);
  const [recaptchaToken, setRecaptchaToken] = useReactState('');
  const [formMessage, setFormMessage] = useReactState('');
  const [formErrorMessage, setFormErrorMessage] = useReactState('');
  const [isHiddenError, setHiddenError] = useReactState(false);

  const isSentAction = useRef(false);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    register,
    trigger,
    reset,
    setError,
    getValues,
    formState: { errors, isValid },
  } = useForm<ContactDetails>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(contactDataSchema),
  });

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }

    const token = await executeRecaptcha('contactUs');
    setRecaptchaToken(token);
  }, [executeRecaptcha, setRecaptchaToken]);

  useEffect(() => {
    if (page === Page.ContactUs) {
      handleReCaptchaVerify().then();
    }
  }, [handleReCaptchaVerify, page]);

  const postTradeInEmail = useMemo(() => {
    return <a href="mailto:online@macroev.com">online@macroev.com</a>;
  }, []);

  const prepareRegisterProps = useCallback(
    (name: keyof ContactDetails) => {
      return () => {
        const registerProps = { ...register(name) };
        const onChange = registerProps.onChange;
        registerProps.onChange = (e) => {
          return onChange(e).then(() => {
            if (!errors[name]) {
              setContactData({
                ...contactData,
                [name]: e.target.value,
              });
            }
          });
        };
        return registerProps;
      };
    },
    [errors, register, contactData, setContactData]
  );

  const onTextareaChange = useCallback(
    async (e) => {
      await trigger('message');
      if (!errors.message) {
        setContactData({
          ...contactData,
          message: e.target.value,
        });
      }
    },
    [errors, contactData, setContactData, trigger]
  );

  const onFieldBlur = useCallback((fieldName: keyof ContactDetails) => () => trigger(fieldName), [trigger]);

  useEffect(() => {
    if (contactData !== initialState) {
      setFormMessage('');
    }
  }, [contactData, setFormMessage]);

  useEffect(() => {
    if (contactData !== initialState) {
      setFormErrorMessage('');
    }
  }, [contactData, setFormErrorMessage]);

  const error = useCallback(
    (key: keyof ContactDetails) => {
      // TODO: get better types
      const obj = (errors[key] as { type: string; message: string }) || undefined;
      return obj?.type === 'required' ? obj?.message || 'Field is required' : obj?.message || '';
    },
    [errors]
  );

  const onSubmit = useCallback(
    async (e) => {
      dispatch('recaptchaKey');
      e.preventDefault();
      e.stopPropagation();

      setFormErrorMessage('');
      setHiddenError(false);

      if (isSentAction.current) {
        return;
      }

      if (!recaptchaToken) {
        setFormErrorMessage('An error appeared with ReCaptcha Token.');
        return;
      }

      isSentAction.current = true;

      const formData = {
        ...getValues(),
        g_recaptcha_response: recaptchaToken,
      };

      dispatch('update', {
        requestSent: true,
      });

      await sendContact(formData)
        .then((data) => {
          dispatch('update', {
            requestSent: false,
          });

          isSentAction.current = false;
          if (data.errors) {
            const errors = data.errors || {};
            Object.keys(data.errors).forEach((fieldName) => {
              const contactFormField = fieldName as keyof ContactDetails;
              const fieldErrors = errors[contactFormField];
              const errorMessage = fieldErrors ? fieldErrors.join(' ') : `The ${contactFormField} field is invalid.`;
              setError(contactFormField, { type: 'custom', message: errorMessage });
            });
            if (data.errors.g_recaptcha_response) {
              setFormErrorMessage(data.errors.g_recaptcha_response[0]);
              setHiddenError(true);
            } else {
              setFormErrorMessage(data.message);
            }
          } else if (data.responseStatus === 429) {
            setFormErrorMessage(data.message);
            setHiddenError(true);
          } else {
            setFormMessage(data.message);
            setContactData(initialState);
            reset();
          }
        })
        .catch((e) => {
          console.error(e);
          setFormErrorMessage('An error appeared when tried to send a message.');
          isSentAction.current = false;
        });

      handleReCaptchaVerify().then(() => {
        // Get new recaptcha Key on successful form submit
        dispatch('recaptchaKey');
      });
    },
    [
      reset,
      dispatch,
      setError,
      getValues,
      handleReCaptchaVerify,
      recaptchaToken,
      setContactData,
      setFormMessage,
      setHiddenError,
      setFormErrorMessage,
    ]
  );

  if (page !== Page.ContactUs) {
    return null;
  }

  return (
    <div className={`${styles.ContactUs} container flex flex-col`} data-testid="ContactUsPage">
      <div className="mb-6">
        <h2 className="text-center">CONTACT US</h2>
      </div>
      <div className="row">
        <div className="col-12 col-md-6">
          <section>
            <p>
              <strong>Postal Address:</strong>
              <br />
              Macro Evolution Services
              <br />
              MacroEv Services Ltd. (Trade-in)
              <br />
              21 Fonthill Business Park,
              <br />
              D22 FR82
              <br />
              Dublin 22
              <br />
              Ireland
            </p>
            <p>
              <strong>Phone:</strong> +353 1 4193120
            </p>
            <p>
              <strong>Email:</strong> {postTradeInEmail}
            </p>
            <p>
              <strong>Office Hours:</strong>
              <br />
              Monday to Friday
              <br />
              9am to 5pm excluding bank holidays.
            </p>
          </section>
        </div>
        <div className="col-12 col-md-6">
          <section>
            <form className="w-full" onSubmit={onSubmit}>
              <input id="site" type="hidden" value={config.tradeInSource} {...register('site')} />
              <div>
                <Field
                  error={error('name')}
                  label={'Name *'}
                  onBlur={onFieldBlur('name')}
                  register={prepareRegisterProps('name')}
                />
              </div>
              <div>
                <Field
                  error={error('email')}
                  label={'Your email *'}
                  onBlur={onFieldBlur('email')}
                  register={prepareRegisterProps('email')}
                />
              </div>
              <div>
                <Field
                  error={error('phone')}
                  label={'Your phone'}
                  onBlur={onFieldBlur('phone')}
                  register={prepareRegisterProps('phone')}
                />
              </div>
              <div>
                <div className="mb-6">
                  <div>
                    <label className="block text-gray-500 font-bold mb-1 md:mb-0 pr-4" htmlFor="textarea_message">
                      Message *:
                    </label>
                  </div>
                  <div>
                    <div>
                      <textarea
                        {...register('message')}
                        onBlur={onFieldBlur('message')}
                        onInput={onTextareaChange}
                        className={`${styles.Textarea} appearance-none border-2 border-gray-200 rounded w-full py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-blue-500`}
                        id="textarea_message"
                        placeholder="Your message..."
                      />
                    </div>
                    <div className="pt-2 text-red-400">
                      <div key="form-error">{error('message')}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <button className="btn btn-secondary" disabled={(!isValid && !isHiddenError) || requestSent}>
                  Submit
                </button>
              </div>
              <div className="text-center mt-3">{formMessage}</div>
              <div className="text-center text-red-400 mt-3">{formErrorMessage}</div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
